import type React from "react";
import { useState } from "react";

import type { IconName } from "@similarweb/icons";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import type { IData } from "app/common/keyword-research/website-keywords/websiteKeywordsPageTypes";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";
import { VOLUME_AND_NO_CLICKS_ENDPOINT } from "app/common/keyword-research/keywords-overview/volumeAndClicksConsts";
import type { IVolumeAndNoClicksResponseData } from "app/common/keyword-research/keywords-overview/volumeAndClicksTypes";
// eslint-disable-next-line max-len
import { getApiParams as getApiParamsForVolumeAndClicksData } from "app/common/keyword-research/keywords-overview/volumeAndClicksUtils";
import CountryService from "app/services/CountryService";
import { DefaultFetchService } from "app/services/fetchService";

import { useKeywordGroupById } from "app/services/keyword-groups";

import { AIAgentErrorCode, AIAgentErrorMessage } from "./AIAgentErrorMessage";
import { MAX_KEYWORD_COUNT_THRESHOLD, MIN_CLICKS_THRESHOLD, MIN_VOLUME_THRESHOLD } from "./constants";
import { DomainsFilter } from "./DomainsFilter";
import { SelectionBox } from "./SelectionBox";
import {
    LoadingSpinner,
    LoadingContainer,
    Container,
    SectionTitle,
    SectionText,
    SectionTextBold,
    ListItem,
    Divider,
    SelectionSection,
    SelectionLabel,
    ActionSection,
    SelectionTextWrapper,
    TextContainer,
    SectionTextMessage,
} from "./StyledComponents";
import type { ISelectedDomain, ISEOAnalysisFormProps } from "./types";
import { logErrorToSentry, logErrorToTrackers } from "./utils";

const SEOAnalysisForm: React.FC<ISEOAnalysisFormProps> = ({
    isKeywordGroup,
    keyword,
    country,
    webSource,
    onDomainSelect,
    onDataSufficiencyCheck,
}) => {
    const decodedKeyword = decodeURIComponent(keyword);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [enoughData, setEnoughData] = useState<boolean>(true);
    const [selectedDomain, setSelectedDomain] = useState<string>("");
    const durationObject = DurationService.getDurationData(BasicDurations.LAST_MONTH).forAPI;
    const { to, from } = durationObject;
    const keywords: string = isKeywordGroup ? keyword.substring(1) : keyword;
    const countryObject = CountryService.getCountryById(country);

    const { data: keywordObject } = useKeywordGroupById(keyword.slice(1));

    const text = isKeywordGroup ? keywordObject?.Name : decodedKeyword;
    const icon = isKeywordGroup ? "nav-keyword-group" : ("search" as IconName);
    const secondaryTextKey = isKeywordGroup
        ? "keywords.query.bar.edit.keyword.group.title"
        : "keywords.query.bar.edit.keyword.title";

    const getVolume = (keys: string, data: IVolumeAndNoClicksResponseData) => {
        return data.Data[keys].Volume;
    };

    const getVolumeData = async (): Promise<number> => {
        const { volumeAndNoClicksParams } = await getApiParamsForVolumeAndClicksData(
            isKeywordGroup,
            keywords,
            durationObject,
            country,
            webSource,
        );

        const fetchService = DefaultFetchService.getInstance();
        const volumeAndNoClicksPromise = fetchService.get<IVolumeAndNoClicksResponseData>(
            VOLUME_AND_NO_CLICKS_ENDPOINT,
            volumeAndNoClicksParams,
        );

        const volumeAndNoClicksData = await volumeAndNoClicksPromise;
        return getVolume(keywords, volumeAndNoClicksData);
    };

    const getClicksData = async (domain: string): Promise<number> => {
        const SERVER_API = "/widgetApi/WebsiteAnalysisV2/WebsiteAnalysis/Table";

        const paramsAsString = new URLSearchParams({
            country: country.toString(),
            to,
            from,
            keys: domain,
            page: "1",
            webSource: webSource,
        }).toString();

        const requestBody = [
            {
                field: "keyword",
                type: "Include" as const,
                items: [
                    {
                        type: isKeywordGroup ? "keywordList" : "keyword",
                        value: isKeywordGroup ? keywords : decodedKeyword,
                    },
                ],
                matchType: "Exact" as const,
            },
        ];

        const clicksData = await DefaultFetchService.getInstance().post<IData>(
            `${SERVER_API}?${paramsAsString}`,
            requestBody,
        );
        return clicksData?.Header?.TotalClicks || 0;
    };

    const checkDataSufficiency = (volume: number, clicks: number): boolean => {
        const isLowVolume = volume < MIN_VOLUME_THRESHOLD;
        const isLowClicks = clicks < MIN_CLICKS_THRESHOLD;
        const isSmallKeywordSet =
            !isKeywordGroup ||
            (isKeywordGroup &&
                (Array.isArray(keywordObject?.Keywords) ? keywordObject.Keywords.length : 0) <
                    MAX_KEYWORD_COUNT_THRESHOLD);

        return !(isLowVolume && isLowClicks && isSmallKeywordSet);
    };

    const checkIfEnoughData = async (domain: string): Promise<boolean> => {
        try {
            const [volume, clicks] = await Promise.all([getVolumeData(), getClicksData(domain)]);

            return checkDataSufficiency(volume, clicks);
        } catch (error) {
            logErrorToSentry("Error checking if enough data:", error as Error);
            logErrorToTrackers("Error checking if enough data", error as Error);
            return false;
        }
    };

    const handleDomainSelect = async (domain: ISelectedDomain | null) => {
        if (domain) {
            try {
                setIsLoading(true);
                const enoughDataResult = await checkIfEnoughData(domain.name);
                if (!enoughDataResult) {
                    allTrackers.dispatchTrackEvent("Pop Up", "open", "SEO Agent/not enough data");
                }
                setIsLoading(false);
                setEnoughData(!!enoughDataResult);
                onDataSufficiencyCheck?.(!!enoughDataResult);
                setSelectedDomain(domain.name);
            } catch (error) {
                setIsLoading(false);
                logErrorToSentry("Error checking if enough data:", error as Error);
                logErrorToTrackers("Error checking if enough data", error as Error);
                setEnoughData(false);
                onDataSufficiencyCheck?.(false);
            }
        }
        if (onDomainSelect) {
            onDomainSelect(domain);
        }
    };

    return (
        <Container>
            {enoughData ? (
                <>
                    <TextContainer>
                        <SectionTitle>{translate("ai.agent.seo.form.title")}</SectionTitle>
                        <SectionText>{translate("ai.agent.seo.form.subtitle")}</SectionText>

                        <Divider />

                        <SectionText>{translate("ai.agent.seo.form.description")}</SectionText>

                        <SectionTextBold>{translate("ai.agent.seo.form.insights.title")}</SectionTextBold>
                        <ListItem>{translate("ai.agent.seo.form.insights.item1")}</ListItem>
                        <ListItem>{translate("ai.agent.seo.form.insights.item2")}</ListItem>
                        <ListItem>{translate("ai.agent.seo.form.insights.item3")}</ListItem>
                    </TextContainer>

                    <SelectionSection>
                        <SelectionLabel>{translate("ai.agent.seo.form.domain.label")}</SelectionLabel>
                        <Flex>
                            <DomainsFilter onDomainSelect={handleDomainSelect} />
                            {isLoading ? (
                                <LoadingContainer>
                                    <LoadingSpinner top="0" />
                                </LoadingContainer>
                            ) : null}
                        </Flex>
                    </SelectionSection>

                    <SelectionSection>
                        <SelectionLabel>{translate("ai.agent.seo.form.selection.label")}</SelectionLabel>
                        <SelectionTextWrapper>
                            <SelectionBox
                                text={text ?? ""}
                                icon={icon}
                                secondaryText={translate(secondaryTextKey)}
                            />

                            <SelectionBox
                                text={countryObject.text}
                                icon={country as string}
                                secondaryText="Country"
                                isCountry={true}
                            />
                        </SelectionTextWrapper>
                    </SelectionSection>
                    <SectionTextMessage>
                        {translate("ai.agent.seo.form.date.range.indication.message")}
                    </SectionTextMessage>
                    <ActionSection marginTop={12}>
                        <SelectionLabel>{translate("ai.agent.seo.form.ready.label")}</SelectionLabel>
                        <SectionText>{translate("ai.agent.seo.form.ready.description")}</SectionText>
                    </ActionSection>
                </>
            ) : null}
            {!enoughData ? (
                <AIAgentErrorMessage
                    keyword={keyword}
                    errorCode={AIAgentErrorCode.NOT_ENOUGH_DATA}
                />
            ) : null}
        </Container>
    );
};

export default SEOAnalysisForm;
